/**
 * Sign-in screen — Google Workspace SSO only (doc 147 §8.2).
 */
import { useState } from 'react';
import { Icon } from '../components/Icon.js';
import { ErrorState } from '../components/ErrorState.js';

export interface SignInViewProps {
  /** Triggers the Google sign-in flow; rejects on failure. */
  onSignIn: () => Promise<void>;
}

export function SignInView({ onSignIn }: SignInViewProps): React.JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleSignIn = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await onSignIn();
    } catch (err) {
      const code = (err as { code?: string }).code ?? 'unknown';
      const msg = (err as { message?: string }).message ?? '';
      if (code === 'auth/admin-restricted-operation' || msg.includes('Workspace')) {
        setError('Sign-in failed. A @brisarx.com Workspace account is required.');
      } else {
        setError(`Sign-in failed (${code}). Please try again.`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin-screen">
      <div>
        <div className="topbar-logo" style={{ fontSize: 'var(--fs-xl)' }}>
          Atlas
        </div>
        <p className="editorial" style={{ color: 'var(--brand-subtle)' }}>
          the Brisarx data model, navigable.
        </p>
      </div>
      <p style={{ color: 'var(--text2)', maxWidth: 360 }}>
        Atlas is an internal tool. Sign in with your <strong>@brisarx.com</strong> Workspace
        account to continue.
      </p>
      <button className="btn btn-primary" onClick={handleSignIn} disabled={busy}>
        <Icon name="login" size="sm" />
        {busy ? 'Signing in…' : 'Sign in with Google'}
      </button>
      {error && <ErrorState variant="inline" message={error} />}
    </div>
  );
}
